import React, { useEffect, useState, useCallback } from "react";
import { datadogRum } from "@datadog/browser-rum";
import Dropdown from "../DropDown";
import { fetchWithCache } from "../../CacheDB/cached-Ref-Api";

interface LifecycleStatusDropdownProps {
  selectedStatus: string;
  onStatusChange: (status: string) => void;
  disabled?: boolean;
  className?: string;
  onFocus?: () => void;
}

const LifecycleStatusDropdown: React.FC<LifecycleStatusDropdownProps> = ({
  selectedStatus,
  onStatusChange,
  disabled = false,
  className = "",
  onFocus,
}) => {
  const [statusList, setStatusList] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatusCodes = useCallback(async () => {
    setLoading(true);
    setError(null);
    const startTime = performance.now();

    try {
      const data = await fetchWithCache("group-status", "/ref-data/group-status");

      if (Array.isArray(data)) {
        setStatusList(data);
        datadogRum.addAction("Fetched LifeCycle Status Successfully", {
          count: data.length,
        });
      } else {
        throw new Error("Invalid response structure");
      }
    } catch (err: any) {
      setError("Failed to fetch LifeCycle status. Please try again.");
      console.error("Error fetching LifeCycle status:", err);
      datadogRum.addError("LifeCycle Status API Failed", { error: err });
    } finally {
      setLoading(false);
      const endTime = performance.now();
      datadogRum.addAction("LifeCycle Status API Response Time", {
        duration: endTime - startTime,
      });
    }
  }, []);

  useEffect(() => {
    fetchStatusCodes();
  }, [fetchStatusCodes]);

  const options = statusList.map((status) => ({
    value: status,
    label: status,
  }));

  const handleChange = (value: string) => {
    onStatusChange(value);
    datadogRum.addAction("Participant Status Selected", { status: value });
  };

  return (
    <Dropdown
      options={options}
      selectedValue={selectedStatus || ""}
      onValueChange={handleChange}
      disabled={disabled || loading || !!error}
      className={className}
      placeholder={loading ? "Loading status..." : error ?? "Select Status"}
      onFocus={onFocus}
    />
  );
};

export default LifecycleStatusDropdown;
